import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import logo from "../../assets/Klyropay.png";
import api from "../../services/api";

/* ---------------- EMAIL / MOBILE SCHEMA ---------------- */

const emailSchema = Yup.object({
  email: Yup.string()
    .required("Email or mobile is required")
    .test(
      "email-or-phone",
      "Enter a valid email or mobile number",
      function (value) {
        if (!value) return false;

        // Email pattern
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        // Indian phone pattern (10 digit + optional +91)
        const phoneRegex = /^(?:\+91[\-\s]?)?[6-9]\d{9}$/;

        return emailRegex.test(value) || phoneRegex.test(value);
      }
    ),
});

/* ---------------- PASSWORD SCHEMA ---------------- */

const passwordSchema = Yup.object({
  newPassword: Yup.string()
    .min(8, "Min. 8 characters required")
    .matches(/[A-Z]/, "At least one uppercase letter required")
    .matches(/[a-z]/, "At least one lowercase letter required")
    .matches(/[0-9]/, "At least one number required")
    .matches(/[!@#$%^&*(),.?":{}|<>]/, "At least one special character required")
    .required("New password is required"),

  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "Passwords do not match")
    .required("Please confirm your password"),
});

function OtpInputs({ value, onChange, length = 6 }) {
  const inputs = useRef([]);
  return (
    <div className="flex gap-2.5">
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={el => (inputs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[i] || ""}
          onChange={e => {
            const val = e.target.value.replace(/[^0-9]/g, "");
            const arr = value.split("");
            arr[i] = val;
            onChange(arr.join(""));
            if (val && i < length - 1) inputs.current[i + 1]?.focus();
          }}
          onKeyDown={e => {
            if (e.key === "Backspace" && !value[i] && i > 0) inputs.current[i - 1]?.focus();
          }}
          className="w-11 h-12 text-center rounded-[10px] bg-[#F3F4F5] text-[18px] font-bold text-[#191C1D] outline-none focus:bg-white focus:ring-2 focus:ring-[#4B2364]/30 transition-all"
        />
      ))}
    </div>
  );
}

const EyeIcon = ({ show }) => (
  <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    {show
      ? <><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /><circle cx="12" cy="12" r="3" /></>
      : <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.97 9.97 0 012.563-4.568M6.228 6.228A9.97 9.97 0 0112 5c4.478 0 8.268 2.943 9.542 7a9.97 9.97 0 01-4.423 5.568M6.228 6.228L3 3m3.228 3.228l3.65 3.65M17.772 17.772L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
    }
  </svg>
);

const Spinner = () => (
  <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
    <circle cx="12" cy="12" r="10" strokeOpacity="0.3"/><path d="M12 2a10 10 0 0 1 10 10"/>
  </svg>
);

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(0);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState("");
  const [resetToken, setResetToken] = useState("");
  const timerRef = useRef(null);

  const startTimer = () => {
    setTimer(60);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimer(t => { if (t <= 1) { clearInterval(timerRef.current); return 0; } return t - 1; });
    }, 1000);
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    try { await emailSchema.validate({ email }); } catch (err) { toast.error(err.message); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("identifier", email);
      const res = await api.post("/auth/forgot-password", fd);
      setUserId(res.data?.data?.user_id || "");
      toast.success(res.data?.message || "OTP sent successfully.");
      setStep(2);
      startTimer();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length < 6) { toast.error("Enter 6-digit OTP"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("user_id", userId);
      fd.append("otp", otp);
      const res = await api.post("/auth/verify-reset-otp", fd);
      setResetToken(res.data?.data?.reset_token || res.data?.reset_token || "");
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.message || "Invalid OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    try { await passwordSchema.validate({ newPassword, confirmPassword }, { abortEarly: false }); }
    catch (err) { toast.error(err.inner?.[0]?.message || err.message); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("reset_token", resetToken);
      fd.append("user_id", userId);
      fd.append("password", newPassword);
      fd.append("password_confirmation", confirmPassword);
      await api.post("/auth/reset-password", fd);
      toast.success("Password reset successfully!");
      navigate("/login");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to reset password.");
    } finally {
      setLoading(false);
    }
  };

  const inputBase = "w-full px-4 py-3 rounded-[10px] bg-[#F3F4F5] text-[15px] text-[#191C1D] outline-none focus:bg-white focus:ring-2 focus:ring-[#4B2364]/30 placeholder:text-gray-400 transition-all";
  const btnBase = "w-full py-3.5 rounded-[12px] text-white text-[16px] font-bold bg-gradient-to-r from-[#754F89] to-[#4B2364] shadow-[0px_8px_20px_rgba(75,35,100,0.3)] hover:opacity-90 active:scale-[0.99] transition-all cursor-pointer border-none disabled:opacity-60 flex items-center justify-center gap-2";

  return (
    <div className="flex min-h-screen w-full font-sans">

      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-[38%] flex-col px-12 py-12 pb-4 relative overflow-hidden bg-gradient-to-br from-[#754F89] to-[#4B2364]">
        <div className="absolute -top-16 -right-16 w-80 h-80 rounded-full bg-violet-400/20 blur-3xl pointer-events-none" />
        <div className="absolute bottom-[10%] -left-10 w-64 h-64 rounded-full bg-purple-400/15 blur-3xl pointer-events-none" />
        <div className="relative h-auto max-w-[245px] justify-start">
          <img src={logo} alt="Klyropay" className="w-[200px] h-auto block mb-10" />
        </div>
        <div className="relative z-10 mb-24">
          <div className="space-y-5 mb-12">
            <h1 className="text-white font-extrabold text-[48px] leading-[1.0] tracking-tight">
              The Future of Digital Alchemy
            </h1>
            <p className="text-white/55 text-sm leading-relaxed max-w-[300px]">
              Secure, high-speed financial infrastructure for modern merchants. Manage payments, invoices, and DMT with institutional-grade precision.
            </p>
          </div>
        </div>
        <div className="relative z-10 flex items-center mt-18 gap-5 bg-[#593270] border border-[#5A3371] rounded-[16px] px-6 py-4">
          <div className="w-12 h-12 rounded-[12px] bg-white/20 flex items-center justify-center text-white">
            <svg xmlns="http://www.w3.org/2000/svg" width="26" height="30" fill="#FFFFFF" viewBox="0 0 24 24" stroke="#a78bfa" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
          </div>
          <div>
            <p className="text-white text-[16px] font-semibold">Institutional Grade Security</p>
            <p className="text-purple-100/70 text-[14px] mt-0.5">PCI-DSS compliant infrastructure protecting every transaction.</p>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center bg-white px-4 py-12">
        <div className="w-full max-w-[420px]">

          <div className="flex lg:hidden justify-center mb-8">
            <img src={logo} alt="Klyropay" className="w-24 h-auto" />
          </div>

          {/* Step 1 — Email */}
          {step === 1 && (
            <form onSubmit={handleSendOtp} className="space-y-5">
              <div className="mb-6">
                <h2 className="text-[28px] font-extrabold text-[#191C1D] tracking-tight">Forgot Password?</h2>
                <p className="text-[#7D747F] text-[14px] mt-1.5">Enter your registered email or mobile number and we'll send you a verification code.</p>
              </div>
              <div>
                <label className="block text-[11px] font-bold text-[#4C444E] uppercase tracking-wider mb-1.5">Email or Mobile</label>
                <input
                  type="text" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="john@company.com or 9876543210" required
                  className={inputBase}
                />
              </div>
              <button type="submit" disabled={loading || !email} className={btnBase}>
                {loading && <Spinner />}
                {loading ? "Sending..." : "Send OTP"}
              </button>
              <button type="button" onClick={() => navigate("/login")}
                className="w-full text-[14px] font-semibold text-[#7D747F] hover:text-[#34094D] bg-transparent border-none cursor-pointer transition-colors text-center">
                ← Back to Login
              </button>
            </form>
          )}

          {/* Step 2 — OTP */}
          {step === 2 && (
            <form onSubmit={handleVerifyOtp} className="space-y-5">
              <div className="mb-6">
                <h2 className="text-[28px] font-extrabold text-[#191C1D] tracking-tight">Verify OTP</h2>
                <p className="text-[#7D747F] text-[14px] mt-1.5">
                  We sent a 6-digit code to <span className="font-semibold text-[#34094D]">{email}</span>
                </p>
              </div>
              <div>
                <label className="block text-[11px] font-bold text-[#4C444E] uppercase tracking-wider mb-3">Enter 6-digit OTP</label>
                <OtpInputs value={otp} onChange={setOtp} length={6} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[13px] text-[#7D747F]">
                  {timer > 0 ? `Resend in ${timer}s` : "Didn't receive it?"}
                </span>
                <button type="button" disabled={timer > 0}
                  onClick={() => { setOtp(""); handleSendOtp({ preventDefault: () => {} }); }}
                  className="text-[13px] font-bold text-[#4B2364] disabled:text-[#7D747F] disabled:cursor-not-allowed bg-transparent border-none cursor-pointer transition-colors">
                  Resend OTP
                </button>
              </div>
              <button type="submit" disabled={loading || otp.length < 6} className={btnBase}>
                {loading && <Spinner />}
                {loading ? "Verifying..." : "Verify OTP"}
              </button>
              <button type="button" onClick={() => setStep(1)}
                className="w-full text-[14px] font-semibold text-[#7D747F] hover:text-[#34094D] bg-transparent border-none cursor-pointer transition-colors text-center">
                ← Change Email
              </button>
            </form>
          )}

          {/* Step 3 — New Password */}
          {step === 3 && (
            <form onSubmit={handleResetPassword} className="space-y-5">
              <div className="mb-6">
                <h2 className="text-[28px] font-extrabold text-[#191C1D] tracking-tight">Set New Password</h2>
                <p className="text-[#7D747F] text-[14px] mt-1.5">Choose a strong password for your account.</p>
              </div>
              <div>
                <label className="block text-[11px] font-bold text-[#4C444E] uppercase tracking-wider mb-1.5">New Password</label>
                <div className="relative">
                  <input type={showNew ? "text" : "password"} value={newPassword} onChange={e => setNewPassword(e.target.value)}
                    placeholder="Min. 8 characters" required className={`${inputBase} pr-11`} />
                  <button type="button" onClick={() => setShowNew(v => !v)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer bg-transparent border-none">
                    <EyeIcon show={showNew} />
                  </button>
                </div>
                {newPassword.length > 0 && (
                  <div className="flex gap-1 mt-2">
                    {[1,2,3,4].map(i => (
                      <div key={i} className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                        newPassword.length >= i * 3
                          ? newPassword.length < 6 ? "bg-red-400" : newPassword.length < 10 ? "bg-amber-400" : "bg-emerald-500"
                          : "bg-gray-200"
                      }`} />
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-[11px] font-bold text-[#4C444E] uppercase tracking-wider mb-1.5">Confirm Password</label>
                <div className="relative">
                  <input type={showConfirm ? "text" : "password"} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Repeat password" required className={`${inputBase} pr-11`} />
                  <button type="button" onClick={() => setShowConfirm(v => !v)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer bg-transparent border-none">
                    <EyeIcon show={showConfirm} />
                  </button>
                </div>
                {confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-[12px] text-red-500 mt-1 font-medium">Passwords do not match</p>
                )}
                {confirmPassword && newPassword === confirmPassword && (
                  <p className="text-[12px] text-emerald-500 mt-1 font-medium flex items-center gap-1">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    Passwords match
                  </p>
                )}
              </div>
              <button type="submit" disabled={loading || !newPassword || newPassword !== confirmPassword} className={btnBase}>
                {loading && <Spinner />}
                {loading ? "Resetting..." : "Reset Password"}
              </button>
            </form>
          )}

        </div>
      </div>
    </div>
  );
}
